import { useLayoutEffect } from 'react';
import type { RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/** Normalized intake of IMAGE A (red car close-up), source 1672×941. */
export const INTAKE_A = { x: 0.8, y: 0.61 };
/** Normalized front intake of the red car in IMAGE B (workshop), source 1672×941 (~x1110, y565). */
export const INTAKE_B = { x: 0.66, y: 0.6 };

interface Refs {
  section: RefObject<HTMLElement | null>;
  fgImg: RefObject<HTMLImageElement | null>;
  bgImg: RefObject<HTMLImageElement | null>;
}

const loaded = (img: HTMLImageElement) => (img.complete && img.naturalWidth
  ? Promise.resolve()
  : new Promise<void>((res, rej) => {
    img.addEventListener('load', () => res(), { once: true });
    img.addEventListener('error', () => rej(), { once: true });
  }));

/** Load + decode so the GPU texture exists before the first transformed frame. */
const ready = (img: HTMLImageElement) => loaded(img)
  .then(() => (img.decode ? img.decode().catch(() => undefined) : undefined));

const SCENE_BG = '#0B0C10';

/** IMAGE A object-position X per breakpoint (must match the img classes in Hero.tsx). */
const fgPosX = () => {
  const w = window.innerWidth;
  if (w >= 1024) return 0.5;
  if (w >= 768) return 0.72;
  return 0.78;
};

interface Focal { W: number; H: number; fx: number; fy: number }

/** Map a normalized source point to layer pixels for an object-fit: cover image. */
const mapCover = (
  img: HTMLImageElement,
  box: HTMLElement,
  pt: { x: number; y: number },
  posX: number,
  posY = 0.5,
): Focal => {
  const W = box.clientWidth;
  const H = box.clientHeight;
  const s = Math.max(W / img.naturalWidth, H / img.naturalHeight);
  const rw = img.naturalWidth * s;
  const rh = img.naturalHeight * s;
  return {
    W, H, fx: (W - rw) * posX + pt.x * rw, fy: (H - rh) * posY + pt.y * rh,
  };
};

/**
 * Center-origin camera. Places focal f at screen point (sx, sy) under scale s:
 *   screen = c + s * (f - c) + t   =>   t = target - c - s * (f - c)
 */
const camera = (f: Focal, s: number, k: number) => {
  const cx = f.W / 2;
  const cy = f.H / 2;
  // k = 0 → focal at its natural spot, k = 1 → focal at viewport center
  const sx = f.fx + (cx - f.fx) * k;
  const sy = f.fy + (cy - f.fy) * k;
  return { scale: s, x: sx - cx - s * (f.fx - cx), y: sy - cy - s * (f.fy - cy) };
};

export default function useHeroZoom({ section, fgImg, bgImg }: Refs) {
  useLayoutEffect(() => {
    const root = section.current;
    const fg = fgImg.current;
    const bg = bgImg.current;
    if (!root || !fg || !bg) return undefined;
    let cancelled = false;
    let mm: gsap.MatchMedia | null = null;

    Promise.all([ready(fg), ready(bg)]).then(() => {
      if (cancelled) return;
      const q = gsap.utils.selector(root);
      const fgLayer = q('[data-hero-layer="foreground"]')[0] as HTMLElement;
      const bgLayer = q('[data-hero-layer="background"]')[0] as HTMLElement;
      const ui = q('[data-hero-layer="ui"]')[0] as HTMLElement;
      const veilA = q('[data-hero-veil]')[0] as HTMLElement;
      const veilB = q('[data-hero-bg-shade]')[0] as HTMLElement;
      const bridge = q('[data-hero-bridge]')[0] as HTMLElement;
      const titleB = q('[data-hero-b-item]') as HTMLElement[];
      const backingB = q('[data-hero-b-backing]') as HTMLElement[];

      let fA: Focal;
      let fB: Focal;
      const measure = () => {
        fA = mapCover(fg, fgLayer, INTAKE_A, fgPosX());
        fB = mapCover(bg, bgLayer, INTAKE_B, 0.5);
        root.style.setProperty('--fx', `${fA.fx}px`);
        root.style.setProperty('--fy', `${fA.fy}px`);
        root.style.setProperty('--bx', `${fB.fx}px`);
        root.style.setProperty('--by', `${fB.fy}px`);
      };
      measure();

      mm = gsap.matchMedia();
      mm.add(
        {
          desktop: '(min-width: 1024px) and (prefers-reduced-motion: no-preference)',
          tablet: '(min-width: 768px) and (max-width: 1023px) and (prefers-reduced-motion: no-preference)',
        },
        (ctx) => {
          const { desktop, tablet } = ctx.conditions as Record<string, boolean>;
          let zoomA = 2.6;
          let zoomB = 2.6;
          let dist = 1.1;
          if (desktop) { zoomA = 5.2; zoomB = 4.5; dist = 2.5; }
          if (tablet) { zoomA = 4.2; zoomB = 3.6; dist = 1.8; }
          else if (!desktop) dist = 1.2;

          gsap.set([fgLayer, bgLayer], { transformOrigin: '50% 50%', force3D: true });
          gsap.set(bgLayer, { opacity: 1 });

          const camA = { s: 1, k: 0 };
          const camB = { s: zoomB, k: 1 };
          // Pull-out driver: p 0→1. Scale is interpolated in log space so every
          // step of scroll produces the same *perceived* dolly distance; k stays
          // tied to s exactly as before, so the start/end framing is unchanged.
          const pull = { p: 0 };
          const logZ = Math.log(zoomB);
          const syncB = () => {
            camB.s = Math.exp(logZ * (1 - pull.p));
            camB.k = (camB.s - 1) / (zoomB - 1);
          };
          const applyA = () => gsap.set(fgLayer, camera(fA, camA.s, camA.k));
          const applyB = () => { syncB(); gsap.set(bgLayer, camera(fB, camB.s, camB.k)); };
          // Opaque scene colour on the pin-spacer so no white page shows while pinned.
          const spacer = root.parentElement;
          if (spacer?.classList.contains('pin-spacer')) spacer.style.backgroundColor = SCENE_BG;
          applyA();
          applyB();

          const tl = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              trigger: root,
              start: 'top top',
              end: () => `+=${Math.round(window.innerHeight * dist)}`,
              pin: true,
              pinSpacing: true,
              anticipatePin: 1,
              scrub: true, // direct sync: no catch-up lag at the pin boundary
              onRefreshInit: () => {
                const sp = root.parentElement;
                if (sp?.classList.contains('pin-spacer')) sp.style.backgroundColor = SCENE_BG;
              },
              refreshPriority: 1,
              invalidateOnRefresh: true,
              onRefresh: () => { measure(); applyA(); applyB(); },
            },
          });

          // Timeline units 0 → 1 == scroll progress.
          tl
            .to(ui, { opacity: 0, y: -12, duration: 0.18, ease: 'power1.in' }, 0.1)
            // 12–40% — push into intake A
            .fromTo(camA, { s: 1, k: 0 }, {
              s: zoomA, k: 1, duration: 0.28, ease: 'power2.in', onUpdate: applyA,
            }, 0.12)
            .fromTo(veilA, { opacity: 0 }, { opacity: 1, duration: 0.12, ease: 'power1.in' }, 0.28)
            // 40–48% — dark bridge. Workshop layer is ALWAYS opaque underneath;
            // only the car layer's opacity changes, so nothing is ever transparent.
            .fromTo(bridge, { opacity: 0 }, { opacity: 1, duration: 0.03, ease: 'power1.in' }, 0.4)
            .fromTo(fgLayer, { opacity: 1 }, { opacity: 0, duration: 0.01 }, 0.43)
            .to(bridge, { opacity: 0, duration: 0.05, ease: 'power1.out' }, 0.44)
            // 44/48–91% — pull out of intake B, log-scale, gentle in/out
            .fromTo(pull, { p: 0 }, {
              p: 1, duration: 0.47, ease: 'sine.inOut', onUpdate: applyB,
            }, 0.44)
            .fromTo(veilB, { opacity: 1 }, { opacity: 0, duration: 0.27, ease: 'power1.inOut' }, 0.46)
            // 58–70% — Image B text group reveal during pull-out (screen-fixed overlay)
            .fromTo(titleB, { opacity: 0, y: 10 }, {
              opacity: 1, y: 0, duration: 0.1, ease: 'power1.out', stagger: 0.01,
            }, 0.58)
            // Readability backing accompanies the B text group (same window, no extra trigger)
            .fromTo(backingB, { opacity: 0 }, { opacity: 1, duration: 0.12, ease: 'power1.out' }, 0.58)
            // 91–100% — stable final workshop hold
            .to({}, { duration: 0.09 }, 0.91);

          return () => {
            gsap.set([fgLayer, bgLayer, ui, veilA, veilB, bridge, ...titleB, ...backingB], { clearProps: 'all' });
          };
        },
      );

      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    }).catch(() => { /* image failed: static Hero stays as fallback */ });

    return () => {
      cancelled = true;
      mm?.revert();
    };
  }, [section, fgImg, bgImg]);
}